import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { filter } from 'rxjs/operators';
import { BaseComponent } from '../../../../shared/base-components/base.component';
import { OmnisearchModel } from '../../../../shared/models/search/omnisearch.model';
import { MvdModalService } from '../../../../shared/services/modal/mvd-modal.service';
import { VehicleAddModalComponent, VehicleAddModalData } from '../../../../vehicle/components/shared/vehicle/vehicle-add-modal/vehicle-add-modal.component';
import { VehicleIdentificationType } from '../../../../vehicle/models/enums/vehicle-identification-type.enum';
import { VehicleSearchCriteria } from '../../../../vehicle/search/vehicle-search/vehicle-search-criteria.model';
import { VehicleSearchResult } from '../../../../vehicle/search/vehicle-search/vehicle-search-result.model';
import { VehicleSearchService } from '../../../../vehicle/search/vehicle-search/vehicle-search.service';
import { VehicleGenericSearchCriteria } from './vehicle-generic-search-criteria.model';

@Component({
    selector: 'vehicle-generic-search',
    templateUrl: './vehicle-generic-search.html',
})
export class VehicleGenericSearchComponent extends BaseComponent implements OnChanges {
    @Input() data: any;
    @Input() vehicleIdentType = 1;
    @Input() searchString = '';
    @Output() onVehicleSelected: EventEmitter<OmnisearchModel | number> = new EventEmitter<OmnisearchModel | number>();
    public vehicles: VehicleSearchResult[] = [];
    public validationMessage = '';
    public searchName = '';
    public showAdd = false;
    public isResultsFound = false;

    private vehicleIdentTypes: VehicleIdentificationType = new VehicleIdentificationType();
    private readonly NO_RESULTS_FOUND: number = 4816;

    constructor(private searchService: VehicleSearchService, private modalService: MvdModalService) {
        super();
    }

    public ngOnInit() {
        this.searchName = this.vehicleIdentTypes.FromId(this.vehicleIdentType).displayValue;
    }

    public ngOnChanges() {
        this.handleDataChange(this.data);
    }

    public reset() {
        this.validationMessage = '';
        this.vehicles = [];
        this.showAdd = false;
    }

    public submit(criteria: VehicleGenericSearchCriteria) {
        this.reset();
        this.searchString = criteria.searchString;
        this.searchService.searchVehiclesAdvanced(this.createCriteria(criteria))
            .pipe(filter(result => Array.isArray(result)))
            .subscribe(
                result => {
                    if (result.length > 1) {
                        this.vehicles = result;
                    } else {
                        this.vehicleSelected(result[0]);
                    }

                },
                e => this.displayErrorMessage(e)
            );
    }

    public vehicleSelected(result: VehicleSearchResult) {
        this.onVehicleSelected.emit(this.mapVehicleSearchResultToOmnisearchModel(result));
    }

    public addVehicle(): void {
        const data = new VehicleAddModalData(null, this.searchString, false, 'Add New Vehicle', false);
        this.modalService.open(VehicleAddModalComponent, data).afterClosed()
            .pipe(filter((result: number) => !!result))
            .subscribe(acceptedValue => this.onVehicleSelected.emit(acceptedValue));
    }

    private createCriteria(criteria: VehicleGenericSearchCriteria): VehicleSearchCriteria {
        return {
            vehicleIdentificationTypeId: this.vehicleIdentType,
            vehicleIdentificationValue: criteria.searchString,
            isExact: criteria.isExact
        };
    }

    private displayErrorMessage(e: any) {
        this.validationMessage = e.validationResults[0].errorMessage;
        this.showAdd = e.validationResults[0].state && e.validationResults[0].state.validationResourceId === this.NO_RESULTS_FOUND;
    }

    private handleDataChange(data: any) {
        this.reset();

        if (!data) {
            return;
        }

        if (this.hasValidationResults(data)) {
            this.displayErrorMessage(data);
        } else if (this.hasSingleResult(data)) {
            this.isResultsFound = true;
            this.selectVehicleFromResult(data.result);
        } else if (Array.isArray(data.result)) {
            this.isResultsFound = true;
            this.vehicles = data.result;
        }
    }

    private hasSingleResult(data: any) {
        return Array.isArray(data.result) && data.result.length === 1;
    }

    private hasValidationResults(data: any) {
        return Array.isArray(data.validationResults) && data.validationResults.length > 0;
    }

    private mapVehicleSearchResultToOmnisearchModel(genResult: VehicleSearchResult) {
        const result = new OmnisearchModel();
        result.bodyStyleTypeDisplayValue = genResult.bodyStyle;
        result.makeDisplayValue = genResult.make;
        result.modelDisplayValue = genResult.model;
        result.modelYear = genResult.year;
        result.plateNumber = genResult.plateNumber;
        result.registrationEndDate = genResult.registrationEndDate;
        result.vehicleId = genResult.vehicleId;
        result.vehicleNumber = genResult.vehicleNumber;
        result.vehicleTypeDisplayValue = genResult.vehicleType;
        result.vin = genResult.vin;
        return result;
    }

    private selectVehicleFromResult(result: VehicleSearchResult[]) {
        this.vehicleSelected(result[0]);
    }
}
